// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-misused-promises, @typescript-eslint/no-unsafe-assignment, react/no-unescaped-entities */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Loader, Message, Table } from "semantic-ui-react";
import {
	deletePlotAssociations,
	plotResultsInfoQueryOptions,
	runPlotAssociations,
} from "../../../../apis/dataLayerApi";
import { PlotResultsInfo } from "../../../../apis/types/dataLayerTypes";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";

const VectorDataLayerPlotResults = () => {
	const moduleNavigation = useModuleNavigation();
	const history = useHistory();
	const queryClient = useQueryClient();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const clientId = userAuth.currentClientId!;
	const dataLayerId = moduleNavigation.flightId!; // workaround for hijacking the Flight Wizard

	const canEdit: boolean = useMemo(
		() => userAuth.hasApplicationArea(ApplicationArea.DataLayerWizardCRUD, userAuth.currentClientId) as boolean,
		[userAuth],
	);

	const plotResultsInfoQueryOpts = plotResultsInfoQueryOptions(getTokenSilently, clientId, dataLayerId);

	const {
		data: [headers, plotResultsInfo],
		isFetching: resultsIsLoading,
	} = useQuery(plotResultsInfoQueryOpts);

	const setPlotResultsInfo = (info: PlotResultsInfo) => {
		return queryClient.setQueryData(plotResultsInfoQueryOpts.queryKey, info);
	};

	const { mutateAsync: runPlotAssociationsMutationAsync, isPending: isRunning } = useMutation({
		mutationFn: runPlotAssociations,
		onMutate: () => {
			toast.loading("Running plot associations...", { type: "info", toastId: "toast-loading-pa", autoClose: false });
		},
		onSuccess: (data) => {
			setPlotResultsInfo(data.data);
			toast.success("Plots associated successfully!");
		},
		onSettled: () => {
			toast.dismiss("toast-loading-pa");
		},
		onError: (error) => {
			console.error(error);
			toast.error("Error running plot associations.");
		},
	});

	const { mutateAsync: deletePlotAssociationsMutationAsync, isPending: isDeleting } = useMutation({
		mutationFn: deletePlotAssociations,
		onMutate: () => {
			toast.loading("Deleting plot associations...", { type: "info", toastId: "toast-deleting-pa", autoClose: false });
		},
		onSuccess: () => {
			setPlotResultsInfo([[], {}]);
			toast.success("Plot associations deleted successfully!");
		},
		onSettled: () => {
			toast.dismiss("toast-deleting-pa");
		},
		onError: (error) => {
			console.error(error);
			toast.error("Error deleting plot associations.");
		},
	});

	const rowHeaders = useMemo<[string, number][]>(() => {
		if (!plotResultsInfo) return [];
		// calculate the rowSpans of each row header
		return _.map(plotResultsInfo, (factor, factorName) => [factorName, Object.values(factor).length]);
	}, [plotResultsInfo]);

	const plotTotalsMap = useMemo(() => {
		if (!plotResultsInfo) return {};

		const totalsMap: { [factorName: string]: { [fieldId: string]: number } } = Object.fromEntries(
			_.chain(plotResultsInfo)
				.entries()
				.map(([factorName, factorTreatments]) => {
					const fieldTotals = _.chain(factorTreatments)
						.values()
						.flatMap((fields) => _.entries(fields))
						.reduce<{ [fieldId: string]: number }>((acc, [fieldId, count]) => {
							acc[fieldId] = (acc[fieldId] ?? 0) + count;
							return acc;
						}, {})
						.value();
					return [factorName, fieldTotals];
				})
				.value(),
		);

		return totalsMap;
	}, [plotResultsInfo]);

	const handleGroupPlots = async () => {
		try {
			await runPlotAssociationsMutationAsync({
				getAccessToken: getTokenSilently,
				dataLayerId,
				clientId,
			});
		} catch (error) {
			console.error(error);
		}
	};

	const handleDelete = async () => {
		try {
			await deletePlotAssociationsMutationAsync({ getAccessToken: getTokenSilently, dataLayerId, clientId });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<>
			<div style={{ display: "flex", marginBottom: "8px", marginLeft: "-1em", marginRight: "-1em", gap: "0.5rem" }}>
				<Button
					id="form-button-back"
					compact
					secondary
					onClick={() => {
						history.push(moduleNavigation.createFlightLink(true, null, "data-layer-assoc-fields"));
					}}
				>
					Back
				</Button>
				<div style={{ flexGrow: 1 }} />
				<Button
					id="button-delete"
					style={{ margin: 0 }}
					negative
					compact
					onClick={handleDelete}
					disabled={!canEdit || resultsIsLoading || isRunning || isDeleting || headers.length === 0}
				>
					Delete Associations
				</Button>
				<Button
					id="button-reanalyze"
					style={{ margin: 0 }}
					color="blue"
					compact
					onClick={handleGroupPlots}
					disabled={!canEdit || resultsIsLoading || isRunning || isDeleting}
				>
					Associate Plots
				</Button>
			</div>
			{(resultsIsLoading || isRunning || isDeleting) && <Loader active />}
			{headers.length > 0 && !(resultsIsLoading || isRunning || isDeleting) ? (
				<Table celled structured>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell colSpan={2} />
							{_.map(headers, (field) => (
								<Table.HeaderCell key={field[0]} textAlign="center">
									<Link to={moduleNavigation.createFieldLink(false, field[0].toLowerCase())} target="_blank">
										{field[1]}
									</Link>
								</Table.HeaderCell>
							))}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(rowHeaders, ([factorName, rowSpan]) => {
							let i = 0;
							return _.map(plotResultsInfo[factorName], (perFieldCounts, treatmentName) => (
								<Table.Row key={factorName + treatmentName + "row"}>
									{i++ === 0 && ( // only want to make this cell once per factor
										<Table.Cell
											key={factorName}
											style={{ width: 130, fontWeight: 700 }}
											textAlign="center"
											rowSpan={rowSpan}
										>
											{factorName}
										</Table.Cell>
									)}
									<Table.Cell
										key={factorName + treatmentName}
										style={{ width: 130, fontWeight: 700 }}
										textAlign="center"
									>
										{treatmentName}
									</Table.Cell>
									{_.map(headers, ([fieldId]) => (
										<Table.Cell key={factorName + treatmentName + fieldId} textAlign="center">
											{perFieldCounts[fieldId] ?? "-"} (
											{_.round((perFieldCounts[fieldId] / plotTotalsMap[factorName][fieldId]) * 100, 2)}%)
										</Table.Cell>
									))}
								</Table.Row>
							));
						})}
					</Table.Body>
				</Table>
			) : (
				!(resultsIsLoading || isRunning || isDeleting) && (
					<Message info icon="info circle">
						<Message.Content>No plot associations run click "Associate Plots"</Message.Content>
					</Message>
				)
			)}
		</>
	);
};

export default VectorDataLayerPlotResults;
